import { useMemo } from 'react';

import type { ReportTask } from '../types';
import { usePackageAllowance } from './usePackageAllowance';

interface ReportContract {
  id?: number;
  type?: string;
  currency?: string;
  dolarRate?: number | undefined;
}

interface ReportPackage {
  hours?: number;
  price?: number;
  curency?: 'USD' | 'UZS' | undefined;
}

interface Params {
  tasks: ReportTask[] | undefined;
  contract: ReportContract | null | undefined;
  hourlyPackage: ReportPackage | null | undefined;
  contractId?: number | undefined;
  billingPeriodId?: number | undefined;
  min?: string | undefined;
  max?: string | undefined;
}

export interface ReportTotals {
  taskCount: number;
  tasksDone: number;
  /** Σ run time across the adjustment chain. */
  totalDuration: number;
  /** Σ billable seconds, raw. */
  totalBilled: number;
  /** Σ billed price, raw. */
  totalBilledPrice: number;
  /** Package allowance covering the current view. */
  capSeconds: number;
  capPrice: number;
  /** Raw totals clamped UP to the package allowance (see note below). */
  displayedBilled: number;
  displayedBilledPrice: number;
  isOverBilled: boolean;
  /** Seconds beyond the allowance; 0 when inside it. */
  overtimeSeconds: number;
}

/**
 * The single source of truth for every figure the two report screens print.
 *
 * `ReportsPage` and `ReportClientFormatPage` used to derive their own totals
 * side by side, and they drifted: the client-format document applied the
 * package floor to the billed *time* but not to the billed *price*, so a
 * contract that under-used its package told the client 16,280,381.97 UZS while
 * the internal screen — and the bill actually raised — said 18,150,000.00.
 * Both screens now render from this hook, so they cannot disagree again.
 *
 * The floor is deliberate, not a rounding artefact: the client pays for the
 * whole package whether or not it is consumed (contract clause 2.3), and the
 * money side already reflects that — the tariff is withdrawn from the contract
 * balance in full, once per period. The raw sums stay available for callers
 * that need the un-floored figure.
 */
export function useReportTotals({
  tasks,
  contract,
  hourlyPackage,
  contractId,
  billingPeriodId,
  min,
  max,
}: Params): ReportTotals {
  const allowance = usePackageAllowance({
    contractId,
    billingPeriodId,
    min,
    max,
    contractCurrency: contract?.currency,
    dolarRate: contract?.dolarRate,
    enabled: contract?.type === 'HOURLY',
  });

  return useMemo(() => {
    const rows = tasks ?? [];

    const taskCount = rows.length;
    const tasksDone = rows.reduce((a, t) => a + (t?.status === 'DONE' ? 1 : 0), 0);
    const totalDuration = rows.reduce(
      (a, t) => a + (t?.totalDurationInSeconds ?? t?.durationInSeconds ?? 0),
      0,
    );
    const totalBilled = rows.reduce(
      (a, t) => a + (t?.totalBillableDurationInSeconds ?? t?.billableDurationInSeconds ?? 0),
      0,
    );
    const totalBilledPrice = rows.reduce(
      (a, t) => a + (t?.totalBilledPrice ?? t?.billedPrice ?? 0),
      0,
    );

    // Fall back to the contract's own package while the period list loads, or
    // for a contract that has no periods yet.
    const fallbackSeconds = Number(hourlyPackage?.hours ?? 0) * 3600;
    const fallbackPriceRaw = Number(hourlyPackage?.price ?? 0);
    // Mirrors TasksService.acceptTask: a USD package on a UZS contract converts
    // at the contract's rate; every other combination is used as-is.
    const fallbackPrice =
      contract?.currency === 'UZS' && hourlyPackage?.curency === 'USD' && contract?.dolarRate
        ? fallbackPriceRaw * contract.dolarRate
        : fallbackPriceRaw;

    const hasAllowance = allowance.periodsCovered > 0 && allowance.capSeconds > 0;
    const capSeconds = hasAllowance ? allowance.capSeconds : fallbackSeconds;
    const capPrice = hasAllowance ? allowance.capPrice : fallbackPrice;

    const isHourlyWithPackage = contract?.type === 'HOURLY' && capSeconds > 0;

    return {
      taskCount,
      tasksDone,
      totalDuration,
      totalBilled,
      totalBilledPrice,
      capSeconds,
      capPrice,
      displayedBilled:
        isHourlyWithPackage && totalBilled < capSeconds ? capSeconds : totalBilled,
      displayedBilledPrice:
        isHourlyWithPackage && totalBilledPrice < capPrice ? capPrice : totalBilledPrice,
      isOverBilled: capSeconds > 0 && totalBilled > capSeconds,
      overtimeSeconds: capSeconds > 0 ? Math.max(0, totalBilled - capSeconds) : 0,
    };
  }, [tasks, contract, hourlyPackage, allowance]);
}
